/**
 * Dual-handle price range slider for WLD collection price facets (Storefront filtering).
 * Syncs Shopify `filter.v.price.*` hidden fields; disables fields at full range (no filter).
 */

/**
 * @param {string | undefined} s
 * @returns {number}
 */
function readCents(s) {
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

/**
 * @param {number} cents
 * @returns {string}
 */
function centsToFacetInputValue(cents) {
  if (!Number.isFinite(cents) || cents <= 0) return '';
  const major = Math.round(cents) / 100;
  if (Number.isInteger(major)) return String(major);
  const t = major.toFixed(2);
  return t.replace(/\.?0+$/, '');
}

/**
 * @param {number} cents
 * @param {string} currency
 * @param {string} [locale]
 */
function formatMoneyLabel(cents, currency, locale) {
  const cur = currency || 'SEK';
  const loc = locale || undefined;
  try {
    return new Intl.NumberFormat(loc, {
      style: 'currency',
      currency: cur,
      maximumFractionDigits: 0,
    }).format(cents / 100);
  } catch {
    return String(Math.round(cents / 100));
  }
}

/**
 * @param {number} v
 * @param {number} lo
 * @param {number} hi
 */
function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

/**
 * @param {HTMLElement} root
 */
function initPriceSlider(root) {
  const rangeMax = readCents(root.dataset.rangeMax);
  if (rangeMax <= 0) return;

  const step = Math.max(1, readCents(root.dataset.step) || 100);
  const currency = root.dataset.currency || 'SEK';
  const locale = root.dataset.locale || '';

  const minHidden = root.querySelector('[data-wld-plp-hidden="min"]');
  const maxHidden = root.querySelector('[data-wld-plp-hidden="max"]');
  const minRange = root.querySelector('.wld-price-slider__input--lower');
  const maxRange = root.querySelector('.wld-price-slider__input--upper');
  const minLabel = root.querySelector('[data-wld-plp-price-min-label]');
  const maxLabel = root.querySelector('[data-wld-plp-price-max-label]');

  if (
    !(minHidden instanceof HTMLInputElement) ||
    !(maxHidden instanceof HTMLInputElement) ||
    !(minRange instanceof HTMLInputElement) ||
    !(maxRange instanceof HTMLInputElement) ||
    !minLabel ||
    !maxLabel
  ) {
    return;
  }

  minRange.min = '0';
  minRange.max = String(rangeMax);
  minRange.step = String(step);
  maxRange.min = '0';
  maxRange.max = String(rangeMax);
  maxRange.step = String(step);

  function snap(c) {
    return Math.round(c / step) * step;
  }

  let minCents = clamp(snap(readCents(root.dataset.startMinCents)), 0, rangeMax);
  let maxCents = clamp(snap(readCents(root.dataset.startMaxCents)), 0, rangeMax);
  if (maxCents < minCents) {
    maxCents = rangeMax;
  }

  function syncHiddens() {
    if (minCents <= 0) {
      minHidden.value = '';
      minHidden.disabled = true;
    } else {
      minHidden.disabled = false;
      minHidden.value = centsToFacetInputValue(minCents);
    }
    if (maxCents >= rangeMax) {
      maxHidden.value = '';
      maxHidden.disabled = true;
    } else {
      maxHidden.disabled = false;
      maxHidden.value = centsToFacetInputValue(maxCents);
    }
  }

  function openRangeHighLabel(cents) {
    const loc = locale || undefined;
    try {
      const parts = new Intl.NumberFormat(loc, {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      }).formatToParts(cents / 100);
      const num = parts
        .filter((p) => p.type === 'integer' || p.type === 'group')
        .map((p) => p.value)
        .join('');
      const cur =
        parts.find((p) => p.type === 'currency')?.value ||
        parts.find((p) => p.type === 'literal')?.value ||
        '';
      if (cur) {
        return `${num}+ ${cur}`.trim();
      }
      return `${num}+`;
    } catch {
      const n = Math.round(cents / 100);
      return `${String(n)}+`;
    }
  }

  function updateLabels() {
    minLabel.textContent = formatMoneyLabel(minCents, currency, locale || undefined);
    const atMax = maxCents >= rangeMax;
    maxLabel.textContent = atMax
      ? openRangeHighLabel(rangeMax)
      : formatMoneyLabel(maxCents, currency, locale || undefined);
  }

  function updateTrackFill() {
    const fill = root.querySelector('.wld-price-slider__range');
    if (!(fill instanceof HTMLElement)) return;
    const hi = rangeMax > 0 ? rangeMax : 1;
    const p1 = (minCents / hi) * 100;
    const p2 = (maxCents / hi) * 100;
    fill.style.left = `${p1}%`;
    fill.style.width = `${Math.max(0, p2 - p1)}%`;
  }

  function applyToInputs() {
    minRange.value = String(minCents);
    maxRange.value = String(maxCents);
    updateTrackFill();
  }

  function onMinInput() {
    minCents = clamp(snap(readCents(minRange.value)), 0, rangeMax);
    maxCents = clamp(snap(readCents(maxRange.value)), 0, rangeMax);
    if (minCents > maxCents) {
      maxCents = minCents;
      maxRange.value = String(maxCents);
    }
    minRange.value = String(minCents);
    syncHiddens();
    updateLabels();
    updateTrackFill();
  }

  function onMaxInput() {
    maxCents = clamp(snap(readCents(maxRange.value)), 0, rangeMax);
    minCents = clamp(snap(readCents(minRange.value)), 0, rangeMax);
    if (maxCents < minCents) {
      minCents = maxCents;
      minRange.value = String(minCents);
    }
    maxRange.value = String(maxCents);
    syncHiddens();
    updateLabels();
    updateTrackFill();
  }

  minRange.addEventListener('input', onMinInput);
  maxRange.addEventListener('input', onMaxInput);

  minRange.addEventListener(
    'pointerdown',
    () => {
      minRange.style.zIndex = '5';
      maxRange.style.zIndex = '4';
    },
    { passive: true }
  );
  maxRange.addEventListener(
    'pointerdown',
    () => {
      maxRange.style.zIndex = '5';
      minRange.style.zIndex = '4';
    },
    { passive: true }
  );

  applyToInputs();
  syncHiddens();
  updateLabels();
}

function initAll() {
  document.querySelectorAll('[data-wld-price-slider]').forEach(initPriceSlider);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
